import { baseApi } from '@/Redux/baseApi';
import type { IntegrationDelivery } from '@/Pages/Integrations/integrationsApi';

/**
 * # ADR-0028 Part B, pinned in one file
 *
 * Backend is `src/modules/delivery-aggregators/` — **unmerged**, read directly
 * from the worktree rather than from a summary (UI-WIRING-CHECKLIST.md Part
 * 3). Ten routes across two controllers:
 *
 *   - `delivery-aggregator-orders.controller.ts`: `GET /orders`,
 *     `GET /orders/unreconciled`, `GET /orders/:id`, `POST /orders/:id/accept`,
 *     `POST /orders/:id/reject`, `POST /orders/:id/prep-time`,
 *     `POST /orders/:id/cancel`.
 *   - `delivery-aggregator-mappings.controller.ts`:
 *     `GET .../installations/:installationId/mappings`,
 *     `POST .../installations/:installationId/mappings`,
 *     `POST .../installations/:installationId/mappings/:externalItemId/availability`.
 *
 * `AggregatorOrderStatus` and the `AggregatorOrderLink`/`AggregatorItemMapping`
 * shapes are copied from `prisma/schema.prisma` (models of the same name) and
 * from what each service method actually returns — not from Swagger, which
 * this module does not decorate its responses with either. A rename on that
 * side is a one-file fix here.
 *
 * ## What "accept" actually does, and why the row can still fail after it
 *
 * `POST /orders/:id/accept` confirms the order, sends it to the kitchen AND
 * records the gross payment in one call (`delivery-aggregator-orders.service.ts`
 * `accept()`) — three of `OrdersService`'s own steps plus a `PaymentsService.create`,
 * none of them optimistic on this side. A rejected accept must leave the row
 * exactly where it was (`RECEIVED`), so every mutation here re-reads the
 * server's own refusal text and never assumes success — no cache patch, no
 * status flip, until the next read says so.
 *
 * ## Reads only, on this cache layer
 *
 * Mutations go through the `api` axios client directly from the component that
 * performs it, invalidating the `DeliveryAggregator` tag by hand — the same
 * split `integrationsApi.ts` documents for the same reason: a refusal here is
 * plain English (`BadRequestException`'s own sentence — "Cannot ingest this
 * order — unmapped: ...", "Aggregator order cannot move from RECEIVED to
 * REJECTED"), and reading `error.response.data.message` at the call site
 * surfaces it better than RTK Query's mutation error shape does.
 */

export const DELIVERY_AGGREGATORS_ROUTES = {
  orders: '/delivery-aggregators/orders',
  order: (id: string) => `/delivery-aggregators/orders/${id}`,
  unreconciled: '/delivery-aggregators/orders/unreconciled',
  accept: (id: string) => `/delivery-aggregators/orders/${id}/accept`,
  reject: (id: string) => `/delivery-aggregators/orders/${id}/reject`,
  prepTime: (id: string) => `/delivery-aggregators/orders/${id}/prep-time`,
  cancel: (id: string) => `/delivery-aggregators/orders/${id}/cancel`,
  mappings: (installationId: string) =>
    `/delivery-aggregators/installations/${installationId}/mappings`,
  mappingAvailability: (installationId: string, externalItemId: string) =>
    `/delivery-aggregators/installations/${installationId}/mappings/${encodeURIComponent(externalItemId)}/availability`,
} as const;

/**
 * The four keys `src/common/constants/roles.ts` seeds. `BRANCH_MANAGER` holds
 * all four; `SHIFT_MANAGER` holds `view`/`acceptReject`/`cancel` but
 * deliberately not `manageMappings` — repointing an external item is a
 * menu-configuration decision, not a floor one, per that file's own comment.
 * No `orders.view`-shaped view/manage split here beyond that: `accept_reject`
 * and `cancel` are already separated from `view`, and from each other, because
 * cancelling an order the kitchen may already have is a heavier action than
 * accepting or refusing one that has not reached it yet.
 */
export const DELIVERY_AGGREGATORS_PERMISSIONS = {
  view: 'delivery_aggregators.view',
  acceptReject: 'delivery_aggregators.accept_reject',
  cancel: 'delivery_aggregators.cancel',
  manageMappings: 'delivery_aggregators.manage_mappings',
} as const;

/** `prisma/schema.prisma`'s `AggregatorOrderStatus`, and `ALLOWED` in the service — the only legal transitions. */
export const AggregatorOrderStatus = {
  RECEIVED: 'RECEIVED',
  ACCEPTED: 'ACCEPTED',
  READY_FOR_PICKUP: 'READY_FOR_PICKUP',
  COURIER_ASSIGNED: 'COURIER_ASSIGNED',
  REJECTED: 'REJECTED',
  COMPLETED: 'COMPLETED',
  CANCELLED: 'CANCELLED',
} as const;
export type AggregatorOrderStatus =
  (typeof AggregatorOrderStatus)[keyof typeof AggregatorOrderStatus];

export interface Page<T> {
  items: T[];
  total: number;
  page: number;
  limit: number;
  pages: number;
}

const emptyPage = <T,>(page?: number, limit?: number): Page<T> => ({
  items: [],
  total: 0,
  page: page ?? 1,
  limit: limit ?? 25,
  pages: 1,
});

/** The narrow order projection `findAll`'s own `include` selects — never items, never money. */
export interface AggregatorLinkedOrderRef {
  id: string;
  number: string;
  status: string;
  checkId: string | null;
}

/**
 * `GET /orders` row and `GET /orders/:id` — same shape; `findOne` includes the
 * FULL `Order` record, but this module's `include: { order: true }` does not
 * pull `Order.items` regardless (Prisma does not walk a relation's own
 * relations without saying so a second time), so **this type never carries
 * line items** — see `AggregatorOrderDetail` docs on why the order screen
 * fetches those from `/orders/:id` separately, and what it renders while it
 * has not, or when there turn out to be none.
 */
export interface AggregatorOrderLink {
  id: string;
  tenantId: string;
  branchId: string;
  installationId: string;
  providerKey: string;
  externalOrderId: string;
  orderId: string;
  status: AggregatorOrderStatus;
  /** Provider-reported, reconciliation-only figures (ADR-0028 §B5) — never the check's own totals. Fixed-scale decimal strings; format with `useCurrency`, never `Number()`. */
  grossAmount: string;
  commissionAmount: string;
  netPayoutAmount: string;
  currency: string;
  courierName: string | null;
  courierPhone: string | null;
  /** The provider's whole webhook body, for triage. Can be large; never rendered wholesale. */
  rawPayload: unknown;
  acceptedAt: string | null;
  rejectedAt: string | null;
  readyAt: string | null;
  completedAt: string | null;
  cancelledAt: string | null;
  createdAt: string;
  updatedAt: string;
  order?: AggregatorLinkedOrderRef;
}

export interface ListAggregatorOrdersQuery {
  page?: number;
  limit?: number;
  status?: AggregatorOrderStatus;
  branchId?: string;
  installationId?: string;
}

/** `AggregatorItemMapping`, verbatim off `prisma/schema.prisma`. */
export interface AggregatorItemMapping {
  id: string;
  installationId: string;
  externalItemId: string;
  externalItemName: string | null;
  catalogItemId: string | null;
  catalogVariantId: string | null;
  isAvailable: boolean;
  lastSyncedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface UpsertMappingBody {
  externalItemId: string;
  externalItemName?: string;
  catalogItemId?: string;
  catalogVariantId?: string;
}

export const deliveryAggregatorsApi = baseApi.injectEndpoints({
  endpoints: (build) => ({
    /** `GET /delivery-aggregators/orders` — `delivery_aggregators.view`. */
    aggregatorOrders: build.query<Page<AggregatorOrderLink>, ListAggregatorOrdersQuery | void>({
      query: (params) => ({ url: DELIVERY_AGGREGATORS_ROUTES.orders, params: params ?? undefined }),
      transformResponse: (response: unknown): Page<AggregatorOrderLink> => {
        const p = response as Partial<Page<AggregatorOrderLink>> | null;
        return Array.isArray(p?.items)
          ? {
              items: p!.items,
              total: p?.total ?? p!.items.length,
              page: p?.page ?? 1,
              limit: p?.limit ?? 25,
              pages: p?.pages ?? 1,
            }
          : emptyPage(1, 25);
      },
      providesTags: (result) => [
        { type: 'DeliveryAggregator' as const, id: 'ORDERS' },
        ...(result?.items ?? []).map((i) => ({ type: 'DeliveryAggregator' as const, id: i.id })),
      ],
    }),

    /** `GET /delivery-aggregators/orders/:id` — `delivery_aggregators.view`. */
    aggregatorOrder: build.query<AggregatorOrderLink | null, string>({
      query: (id) => ({ url: DELIVERY_AGGREGATORS_ROUTES.order(id) }),
      transformResponse: (response: unknown) =>
        response && typeof response === 'object' ? (response as AggregatorOrderLink) : null,
      providesTags: (_r, _e, id) => [{ type: 'DeliveryAggregator' as const, id }],
    }),

    /**
     * `GET /delivery-aggregators/orders/unreconciled` — `delivery_aggregators.view`.
     * A bare array, not `{ items }` — `findUnlinkedDeliveries` returns raw rows,
     * not a `paginate()` envelope. Same row shape `integrationsApi.ts` already
     * pins as `IntegrationDelivery`, because it is the same table
     * (`integration_event_deliveries`) — reused rather than redeclared.
     */
    aggregatorUnreconciled: build.query<IntegrationDelivery[], string | void>({
      query: (installationId) => ({
        url: DELIVERY_AGGREGATORS_ROUTES.unreconciled,
        params: installationId ? { installationId } : undefined,
      }),
      transformResponse: (response: unknown) => (Array.isArray(response) ? response : []),
      providesTags: [{ type: 'DeliveryAggregator' as const, id: 'UNRECONCILED' }],
    }),

    /** `GET .../installations/:installationId/mappings` — `delivery_aggregators.view`. */
    aggregatorMappings: build.query<AggregatorItemMapping[], string>({
      query: (installationId) => ({ url: DELIVERY_AGGREGATORS_ROUTES.mappings(installationId) }),
      transformResponse: (response: unknown) => (Array.isArray(response) ? response : []),
      providesTags: (_r, _e, installationId) => [
        { type: 'DeliveryAggregator' as const, id: `mappings:${installationId}` },
      ],
    }),
  }),
  overrideExisting: false,
});

export const {
  useAggregatorOrdersQuery,
  useAggregatorOrderQuery,
  useAggregatorUnreconciledQuery,
  useAggregatorMappingsQuery,
} = deliveryAggregatorsApi;

/** An order link changed status (accept/reject/cancel), or a fresh one arrived. */
export function invalidateAggregatorOrders(id?: string) {
  return deliveryAggregatorsApi.util.invalidateTags([
    { type: 'DeliveryAggregator' as const, id: 'ORDERS' },
    { type: 'DeliveryAggregator' as const, id: 'UNRECONCILED' },
    ...(id ? [{ type: 'DeliveryAggregator' as const, id }] : []),
  ]);
}

/** A mapping was created, edited, or its marketplace availability was toggled. */
export function invalidateAggregatorMappings(installationId: string) {
  return deliveryAggregatorsApi.util.invalidateTags([
    { type: 'DeliveryAggregator' as const, id: `mappings:${installationId}` },
  ]);
}
